/**
 * FilterSel — Tier 0 slice-local primitive (SCRUM-167 epic).
 *
 * Compact pill-styled `<select>` for the filter row in the Properties
 * list (Status / Type / future filters). The bundle's
 * `~/Desktop/design_handoff_properties/PROPERTIES_LIST_SPEC.md` §6 calls
 * it out as a named shared component; building it as a slice primitive
 * from day 1 avoids the "two places use a select differently" trap.
 *
 * The kit doesn't ship a select primitive today. Composing a real
 * native `<select>` (rather than a custom Radix-style listbox) is the
 * right call for filter UX — native is keyboard-accessible, mobile-
 * native (iOS wheel picker), and a11y by default. The visual styling
 * just hides the OS chrome with a brand-tinted chevron overlay.
 *
 * Generic over the option-id type so callers can use string-literal
 * unions (`'all' | 'bookable' | 'non-bookable'`) and get exhaustive
 * narrowing on the `onChange` callback.
 */

import * as React from 'react';
import { Icon } from '@sparta/ui-kit';

export interface FilterSelOption<TId extends string> {
  readonly id: TId;
  readonly label: string;
}

export interface FilterSelProps<TId extends string> {
  /** Visible label for the filter. Rendered as the select's accessible name. */
  readonly label: string;
  /** Currently-selected option id. */
  readonly value: TId;
  /** Called with the next id on selection. */
  readonly onChange: (next: TId) => void;
  /** Option list — at least 2 entries (single-choice selects don't filter). */
  readonly options: ReadonlyArray<FilterSelOption<TId>>;
  /** Optional id for `aria-controls` wiring from a parent. */
  readonly id?: string;
}

export function FilterSel<TId extends string>({
  label,
  value,
  onChange,
  options,
  id,
}: FilterSelProps<TId>): React.ReactElement {
  // The visible label is the *purpose* of the select (e.g. "Status",
  // "Type"); the BE option labels are surfaced once an option is
  // chosen. We use `aria-label` on the select itself so the select's
  // accessible name is the filter purpose — that matches the
  // ACCESSIBILITY.md §1 tab-order expectation ("Status filter" and
  // "Type filter" as named tab stops).
  return (
    <div className="relative inline-block">
      <select
        id={id}
        aria-label={label}
        value={value}
        onChange={(e) => onChange(e.target.value as TId)}
        className="appearance-none h-9 pr-8 pl-3 rounded-full text-sm font-medium cursor-pointer"
        style={{
          background: 'var(--bg-surface)',
          color: 'var(--fg-primary)',
          border: '1px solid var(--border-default)',
          font: 'inherit',
        }}
      >
        {options.map((opt) => (
          <option key={opt.id} value={opt.id}>
            {opt.label}
          </option>
        ))}
      </select>
      <span
        aria-hidden="true"
        className="absolute inset-y-0 right-2.5 flex items-center pointer-events-none"
        style={{ color: 'var(--fg-tertiary)' }}
      >
        <Icon name="chevDown" size={12} />
      </span>
    </div>
  );
}
